// src/components/PrescriptionTab.js
import React, { useState, useCallback } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  IconButton,
  Alert,
  Chip,
  InputAdornment,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import Autocomplete from '@mui/material/Autocomplete';
import { medicationsList } from '../data/medicationsList';

const PrescriptionTab = ({
  prescriptions = [],
  setPrescriptions,
  openPrescriptionModal,
}) => {
  const [newPrescription, setNewPrescription] = useState({
    medication: '',
    medication_name: '', // Backend field
    dosage: '',
    frequency: '',
    instructions: '',
  });
  const [editingIndex, setEditingIndex] = useState(null);
  const [errors, setErrors] = useState({});
  const [showError, setShowError] = useState(false);

  // Normalize prescriptions prop to handle different formats
  const normalizedPrescriptions = React.useMemo(() => {
    if (!prescriptions || prescriptions.length === 0) return [];
    return prescriptions.map(p => ({
      medication: p.medication || p.medication_name || '',
      medication_name: p.medication_name || p.medication || '',
      dosage: p.dosage || '',
      frequency: p.frequency || '',
      instructions: p.instructions || '',
    }));
  }, [prescriptions]);

  const validatePrescription = useCallback((prescription) => {
    const newErrors = {};
    
    if (!prescription.medication || prescription.medication.trim() === '') {
      newErrors.medication = 'Medication name is required';
    }
    
    if (!prescription.dosage || prescription.dosage.trim() === '') {
      newErrors.dosage = 'Dosage is required';
    }
    
    if (!prescription.frequency || prescription.frequency.trim() === '') {
      newErrors.frequency = 'Frequency is required';
    }
    
    return newErrors;
  }, []);

  const handleMedicationChange = useCallback((event, newValue) => {
    const medication = typeof newValue === 'string' ? newValue : (newValue || '');
    setNewPrescription(prev => ({
      ...prev,
      medication: medication,
      medication_name: medication, // Sync both fields
    }));
    // Clear error when user starts typing
    if (errors.medication) {
      setErrors(prev => ({ ...prev, medication: null }));
    }
  }, [errors.medication]);

  const handleTextFieldChange = useCallback((field) => (e) => {
    const value = e.target.value;
    setNewPrescription(prev => ({
      ...prev,
      [field]: value,
      ...(field === 'medication' ? { medication_name: value } : {}), // Sync medication fields
    }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
  }, [errors]);

  const addOrUpdatePrescription = useCallback(() => {
    const validationErrors = validatePrescription(newPrescription);
    
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      setShowError(true);
      return;
    }

    const prescriptionToAdd = {
      medication: newPrescription.medication.trim(),
      medication_name: newPrescription.medication.trim(), // Ensure both fields are set
      dosage: newPrescription.dosage.trim(),
      frequency: newPrescription.frequency.trim(),
      instructions: newPrescription.instructions.trim(),
    };

    if (editingIndex !== null) {
      const updatedPrescriptions = [...normalizedPrescriptions];
      updatedPrescriptions[editingIndex] = prescriptionToAdd;
      setPrescriptions(updatedPrescriptions);
      setEditingIndex(null);
    } else {
      setPrescriptions([...normalizedPrescriptions, prescriptionToAdd]);
    }

    // Reset form
    setNewPrescription({
      medication: '',
      medication_name: '',
      dosage: '',
      frequency: '',
      instructions: '',
    });
    setErrors({});
    setShowError(false);
  }, [newPrescription, editingIndex, normalizedPrescriptions, setPrescriptions, validatePrescription]);

  const editPrescription = useCallback((index) => {
    const prescription = normalizedPrescriptions[index];
    setNewPrescription({
      medication: prescription.medication || '',
      medication_name: prescription.medication_name || prescription.medication || '',
      dosage: prescription.dosage || '',
      frequency: prescription.frequency || '',
      instructions: prescription.instructions || '',
    });
    setEditingIndex(index);
    setErrors({});
    setShowError(false);
  }, [normalizedPrescriptions]);

  const deletePrescription = useCallback((index) => {
    const updatedPrescriptions = normalizedPrescriptions.filter((_, i) => i !== index);
    setPrescriptions(updatedPrescriptions);
    
    // Reset editing if deleting the item being edited
    if (editingIndex === index) {
      setEditingIndex(null);
      setNewPrescription({
        medication: '',
        medication_name: '',
        dosage: '',
        frequency: '',
        instructions: '',
      });
      setErrors({});
      setShowError(false);
    }
  }, [normalizedPrescriptions, setPrescriptions, editingIndex]);

  const handleCancel = useCallback(() => {
    setEditingIndex(null);
    setNewPrescription({
      medication: '',
      medication_name: '',
      dosage: '',
      frequency: '',
      instructions: '',
    });
    setErrors({});
    setShowError(false);
  }, []);

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant='h6'>Prescriptions</Typography>
        {normalizedPrescriptions.length > 0 && (
          <Chip 
            label={`${normalizedPrescriptions.length} medication${normalizedPrescriptions.length !== 1 ? 's' : ''}`} 
            color="primary" 
            size="small" 
          />
        )}
      </Box>

      {showError && Object.keys(errors).length > 0 && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setShowError(false)}>
          Please fix the following errors:
          <ul style={{ margin: '8px 0 0 0', paddingLeft: '20px' }}>
            {Object.values(errors).filter(Boolean).map((error, idx) => (
              <li key={idx}>{error}</li>
            ))}
          </ul>
        </Alert>
      )}

      <Autocomplete
        freeSolo
        options={medicationsList.map((option) => option.name)}
        value={newPrescription.medication}
        onChange={handleMedicationChange}
        onInputChange={(event, newInputValue) => {
          // Handle input changes for freeSolo mode
          if (event && event.type === 'change') {
            handleMedicationChange(event, newInputValue);
          }
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            fullWidth
            label='Medication Name'
            placeholder='Search or enter medication name'
            required
            error={!!errors.medication}
            helperText={errors.medication || 'Enter medication name or select from list'}
            sx={{ marginBottom: 2 }}
          />
        )}
      />
      
      <TextField
        fullWidth
        label='Dosage'
        placeholder='E.g., 500mg, 1 tablet, 10ml'
        required
        value={newPrescription.dosage}
        onChange={handleTextFieldChange('dosage')}
        error={!!errors.dosage}
        helperText={errors.dosage || 'Enter the dosage amount'}
        sx={{ marginBottom: 2 }}
      />
      
      <TextField
        fullWidth
        label='Frequency'
        placeholder='E.g., Once daily, Twice daily, After meals'
        required
        value={newPrescription.frequency}
        onChange={handleTextFieldChange('frequency')}
        error={!!errors.frequency}
        helperText={errors.frequency || 'Enter how often to take the medication'}
        sx={{ marginBottom: 2 }}
      />
      
      <TextField
        fullWidth
        label='Instructions'
        placeholder='Special instructions (optional)'
        multiline
        rows={2}
        value={newPrescription.instructions}
        onChange={handleTextFieldChange('instructions')}
        helperText="Any additional instructions for the patient"
        sx={{ marginBottom: 2 }}
      />
      
      <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
        <Button
          variant='contained'
          color='primary'
          startIcon={editingIndex !== null ? <EditIcon /> : <AddIcon />}
          onClick={addOrUpdatePrescription}
        >
          {editingIndex !== null ? 'Update Prescription' : 'Add Prescription'}
        </Button>
        {editingIndex !== null && (
          <Button
            variant='outlined'
            onClick={handleCancel}
          >
            Cancel
          </Button>
        )}
      </Box>

      {normalizedPrescriptions.length > 0 ? (
        <Box sx={{ marginTop: 3 }}>
          {normalizedPrescriptions.map((prescription, index) => (
            <Paper
              key={index}
              elevation={2}
              sx={{
                padding: 2,
                marginBottom: 1.5,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-start',
                border: editingIndex === index ? '2px solid' : '1px solid',
                borderColor: editingIndex === index ? 'primary.main' : 'divider',
                transition: 'all 0.2s',
                '&:hover': {
                  boxShadow: 4,
                },
              }}
            >
              <Box sx={{ flex: 1 }}>
                <Typography variant='subtitle1' sx={{ fontWeight: 600, mb: 0.5 }}>
                  {prescription.medication || prescription.medication_name || 'Unnamed Medication'}
                </Typography>
                <Typography variant='body2' color="text.secondary">
                  <strong>Dosage:</strong> {prescription.dosage || 'Not specified'}
                </Typography>
                <Typography variant='body2' color="text.secondary">
                  <strong>Frequency:</strong> {prescription.frequency || 'Not specified'}
                </Typography>
                {prescription.instructions && (
                  <Typography variant='body2' color="text.secondary" sx={{ mt: 0.5 }}>
                    <strong>Instructions:</strong> {prescription.instructions}
                  </Typography>
                )}
              </Box>
              <Box sx={{ display: 'flex', gap: 0.5 }}>
                <IconButton
                  color='primary'
                  size="small"
                  onClick={() => editPrescription(index)}
                  disabled={editingIndex !== null && editingIndex !== index}
                >
                  <EditIcon />
                </IconButton>
                <IconButton
                  color='error'
                  size="small"
                  onClick={() => deletePrescription(index)}
                >
                  <DeleteIcon />
                </IconButton>
              </Box>
            </Paper>
          ))}
        </Box>
      ) : (
        <Paper sx={{ p: 3, textAlign: 'center', bgcolor: 'grey.50' }}>
          <Typography variant='body2' color="text.secondary">
            No prescriptions added yet. Add a medication above.
          </Typography>
        </Paper>
      )}
    </Box>
  );
};

export default PrescriptionTab;
